import type { Tab } from '~/components/VerticalTabs'
import type { IAppSummary } from '~/types/application'
import type { IAsset } from '~/types/assets-management'
import type { DeviceType } from '~/types/device-type'
import type { IIap } from '~/types/iap'
import type { IAssetOption, IIapOption, ITranslationOption } from '~/types/option'
import type {
  IPageContainer,
  IPageContainerCreateUpdate,
  IPageContainerDetails,
  IPageContainerFormError,
  IPlaceholderFilters,
  IPlaceholderMap,
} from '~/types/page-container'
import type {
  IPageContainerFormValues,
  IPlaceholderConfig,
  IPlaceholderFormValue,
  ITemplate,
  ITemplateConfigOptions,
  ITemplatePageConfig,
  IPlaceholderRule,
  IEmbeddedUiFormValue,
  IPlaceholderType,
} from '~/types/template'
import type { ITranslationKeyValue } from '~/types/translation'

import { PAGE_CONTAINER_DEFAULT_TITLE } from '~/const/page-containers'
import { DEFAULT_IAP_PRICE_COUNTRY } from '~/const/template'
import { PAGE_CONTAINER_STATUS } from '~/types/page-container'
import { PLACEHOLDER_RULE_TYPES } from '~/types/template'
import PlaceholderTypeIcon from '~/views/DynamicScreens/PageContainers/Details/Form/Editor/Icon'

export const formatPageContainerData = ({
  assets,
  iaps,
  pageContainer,
  translations,
}: {
  assets: IAsset[]
  iaps?: IIap[]
  pageContainer: IPageContainer
  translations: ITranslationKeyValue[]
}): IPageContainerDetails => {
  const formatColorInput = (deviceType: string, color?: string, isUnity?: boolean): string | undefined => {
    if (color && color.length > 7 && deviceType === 'android' && !isUnity) {
      const alpha = color.substring(1, 3)
      return `${color[0]}${color.substring(3, color.length)}${alpha}`
    }
    return color
  }

  const placeholders: IPlaceholderMap = {}

  const {
    placeholder_actions_values,
    placeholder_asset_values,
    placeholder_color_values,
    placeholder_embedded_ui_values,
    placeholder_iap_values,
    placeholder_text_values,
  } = pageContainer

  placeholder_asset_values &&
    placeholder_asset_values.forEach(({ id, placeholder }) => {
      const asset = assets.find(({ id: assetId }) => assetId === id)
      placeholders[placeholder] = {
        ...placeholders[placeholder],
        asset:
          id && asset
            ? {
                fileType: asset.file_type,
                files: asset.files,
                id,
                label: asset.name,
                status: asset.status,
                updatedAt: asset.updated_at,
                updatedBy: asset.updated_by,
                value: id,
              }
            : undefined,
      }
    })

  placeholder_text_values &&
    placeholder_text_values.forEach(({ placeholder, text, translation_key }) => {
      const translation = translations.find(({ key }) => key === translation_key)
      placeholders[placeholder] = {
        ...placeholders[placeholder],
        translation: { label: translation_key, params: [], text, translation, value: translation_key },
      }
    })

  placeholder_color_values &&
    placeholder_color_values.forEach(({ placeholder, value }) => {
      placeholders[placeholder] = {
        ...placeholders[placeholder],
        color: formatColorInput(pageContainer.device_type, value, pageContainer.is_unity),
      }
    })

  placeholder_actions_values &&
    placeholder_actions_values.forEach(({ placeholder, value }) => {
      placeholders[placeholder] = { ...placeholders[placeholder], action: value }
    })

  placeholder_iap_values &&
    placeholder_iap_values.forEach(({ id, placeholder }) => {
      const iap = iaps?.find(({ sku }) => sku === id)
      const { prices, sku, status, title } = iap || {}
      const price = prices && prices[DEFAULT_IAP_PRICE_COUNTRY]
      placeholders[placeholder] = {
        ...placeholders[placeholder],
        iap: { label: title || '', price, status: status || '', value: sku || '' },
      }
    })

  placeholder_embedded_ui_values &&
    placeholder_embedded_ui_values.forEach(({ iaps: embeddedIaps, id, navigation_targets, placeholder }) => {
      placeholders[placeholder] = {
        ...placeholders[placeholder],
        embeddedUi: {
          iaps: embeddedIaps.map((id) => {
            const iap = iaps?.find(({ sku }) => sku === id)
            const { prices, sku, status, title } = iap || {}
            const price = prices && prices[DEFAULT_IAP_PRICE_COUNTRY]
            return { label: title || '', price, status: status || '', value: sku || '' }
          }),
          id,
          navigationTargets: navigation_targets,
        },
      }
    })

  return { ...pageContainer, placeholders }
}

export const formatPageContainerFormValues = (values: IPageContainerFormValues): IPageContainerCreateUpdate => {
  const formatColorOutput = (color: string, deviceType?: string, isUnity?: boolean): string => {
    if (color.length > 7 && deviceType === 'android' && !isUnity) {
      const alpha = color.substring(color.length - 2, color.length)
      return `${color[0]}${alpha}${color.substring(1, color.length - 2)}`
    }
    return color
  }

  const placeholdersData: Partial<IPageContainerCreateUpdate> = {
    actions: [],
    assets: [],
    colors: [],
    embedded_uis: [],
    iaps: [],
    translations: [],
  }

  const { placeholders, selectedApp, ...data } = values

  placeholders.forEach(async ({ key, type, value }) => {
    const placeholder = key
    switch (type) {
      case 'translation':
        const translation_key = value ? (value as ITranslationOption).value : ''
        const text = value ? (value as ITranslationOption).text : ''
        translation_key && placeholdersData.translations?.push({ placeholder, text, translation_key })
        break
      case 'asset':
        const id = value ? (value as IAssetOption).value : ''
        id && placeholdersData.assets?.push({ id, placeholder })
        break
      case 'color':
        const color = (value as string) || ''
        color &&
          placeholdersData.colors?.push({
            placeholder,
            value: formatColorOutput(color, selectedApp?.device_type, values.isUnity),
          })
        break
      case 'action':
        const action = (value as string) || ''
        action && placeholdersData.actions?.push({ placeholder, value: action })
        break
      case 'iap':
        const iap = (value as IIapOption) || ''
        iap && placeholdersData.iaps?.push({ id: iap.value, placeholder })
        break
      case 'embeddedUi':
        const embeddedUi = (value as IEmbeddedUiFormValue) || undefined
        embeddedUi &&
          placeholdersData.embedded_uis?.push({
            iaps: embeddedUi.iaps?.map(({ value }) => value).filter((value) => !!value) || [],
            id: embeddedUi.id || '',
            navigation_targets: embeddedUi.navigationTargets || [],
            placeholder: key,
          })
        break
      default:
        return
    }
  })

  return { ...data, ...placeholdersData, appKey: selectedApp?.app_key, deviceType: selectedApp?.device_type }
}

export const getInitialValues = ({
  isUnity,
  pageContainerDetails,
  pageContainerTemplate,
  selectedApp,
  templateConfig,
  templateVersion,
}: {
  isUnity: boolean
  pageContainerDetails?: IPageContainerDetails
  pageContainerTemplate?: ITemplate
  selectedApp: IAppSummary
  templateConfig?: ITemplateConfigOptions
  templateVersion?: string
}): IPageContainerFormValues => {
  const placeholders = getPlaceholderFormValues({
    appDeviceType: selectedApp?.device_type,
    placeholderMap: pageContainerDetails?.placeholders,
    placeholderRules: pageContainerTemplate?.placeholders?.map(({ rules }) => rules || []).flat(),
    templateConfig: templateConfig && templateConfig.config,
  })

  if (pageContainerDetails) {
    return {
      isDisabled: pageContainerDetails.status !== PAGE_CONTAINER_STATUS.DRAFT,
      isUnity: pageContainerDetails.is_unity,
      placeholders,
      screenshotFileIds: pageContainerDetails.screenshot_file_ids || [],
      selectedApp,
      templateKey: pageContainerDetails.template_key,
      templateVersion: pageContainerDetails.template_version,
      title: pageContainerDetails.title,
    }
  }

  return {
    isUnity,
    placeholders,
    screenshotFileIds: [],
    selectedApp,
    templateKey: templateConfig && templateConfig.key,
    templateVersion: templateVersion || 'unknown',
    title: PAGE_CONTAINER_DEFAULT_TITLE,
  }
}

export const getPlaceholderFormValuesByType = (
  placeholderConfig: IPlaceholderConfig,
  placeholderMap?: IPlaceholderMap,
  placeholderRules?: IPlaceholderRule[]
): Omit<IPlaceholderFormValue, 'fieldName'>[] => {
  const { category, className, defaultValue, hiddenFields, iapKey, placeholderKey, type } = placeholderConfig
  const types = Array.isArray(type) ? type : [type]
  return types.map((t) => {
    const valueFromConfig = defaultValue && defaultValue[t]
    const valueFromMap = placeholderMap && placeholderMap[placeholderKey] && placeholderMap[placeholderKey][t]
    const embeddedUiValue =
      type === 'embeddedUi' ? { iaps: undefined, id: '', navigationTargets: undefined } : undefined
    const value = valueFromMap || valueFromConfig || embeddedUiValue || ''
    const rules = placeholderRules?.filter(({ placeholder_type }) => placeholder_type === t)

    return {
      category,
      className,
      hiddenFields,
      iapKey,
      key: placeholderKey,
      rules,
      type: t,
      value,
    }
  })
}

export const getPlaceholderFormValues = ({
  appDeviceType,
  placeholderMap,
  placeholderRules,
  templateConfig,
}: {
  appDeviceType?: DeviceType
  placeholderMap?: IPlaceholderMap
  placeholderRules?: IPlaceholderRule[]
  templateConfig?: ITemplatePageConfig[]
}): IPlaceholderFormValue[] => {
  if (templateConfig) {
    return (
      templateConfig
        .reduce(
          (values, { placeholders }) => {
            placeholders.forEach(({ deviceType, ...placeholderConfig }) => {
              if (!deviceType || deviceType === appDeviceType) {
                const rules =
                  placeholderRules &&
                  placeholderRules?.filter(
                    ({ placeholder_key }) => placeholderConfig.placeholderKey === placeholder_key
                  )
                const placeholderValues = getPlaceholderFormValuesByType(placeholderConfig, placeholderMap, rules)
                values.push(...placeholderValues)
              }
            })
            return values
          },
          [] as Omit<IPlaceholderFormValue, 'fieldName'>[]
        )
        // NOTE: filter out placeholders that are duplicated as a result of being in multiple template pages
        .filter(
          (placeholder, index, self) =>
            index === self.findIndex(({ key, type }) => key === placeholder.key && type === placeholder.type)
        )
        .reduce((values, placeholder, index) => {
          values.push({
            ...placeholder,
            fieldName: `placeholders.${index}.value`,
          })
          return values
        }, [] as IPlaceholderFormValue[])
    )
  } else if (placeholderMap) {
    return Object.entries(placeholderMap)
      .reduce(
        (values, [placeholderKey, placeholderValues]) => {
          Object.entries(placeholderValues).forEach(([placeholderType, placeholderValue]) => {
            values.push({
              category: 'Unknown',
              key: placeholderKey,
              type: placeholderType as IPlaceholderType,
              value: placeholderValue,
            })
          })
          return values
        },
        [] as Omit<IPlaceholderFormValue, 'fieldName'>[]
      )
      .filter(
        (placeholder, index, self) =>
          index === self.findIndex(({ key, type }) => key === placeholder.key && type === placeholder.type)
      )
      .reduce((values, placeholder, index) => {
        values.push({
          ...placeholder,
          fieldName: `placeholders.${index}.value`,
        })
        return values
      }, [] as IPlaceholderFormValue[])
  }
  return []
}

export const isPlaceholderFormValueCompleted = ({ type, value }: IPlaceholderFormValue): boolean => {
  if (type === 'embeddedUi') {
    const { id } = value as IEmbeddedUiFormValue
    if (!id) return false
    return true
  }
  return !!value
}

export const isPlaceholderCompleted = (key: string, values: IPlaceholderFormValue[]): boolean => {
  return values.filter(({ key: placeholderKey }) => placeholderKey === key).every(isPlaceholderFormValueCompleted)
}

export const isFormCompleted = (placeholders: IPlaceholderFormValue[]): boolean => {
  return placeholders.every(isPlaceholderFormValueCompleted)
}

export const filterPlaceholdersConfig = ({
  errors,
  filters: userFilters,
  placeholderFormValues,
  placeholdersConfig,
  searchText,
  templateFilter,
}: {
  errors: IPageContainerFormError[]
  filters: IPlaceholderFilters
  placeholderFormValues: IPlaceholderFormValue[]
  placeholdersConfig: IPlaceholderConfig[]
  searchText: string
  templateFilter?: string
}): IPlaceholderConfig[] =>
  placeholdersConfig.filter(({ filters, placeholderKey }) => {
    if (!placeholderFormValues.some(({ key }) => key === placeholderKey)) {
      return false
    }

    if (!placeholderKey.includes(searchText)) {
      return false
    }

    if (userFilters?.uncompletedOnly && isPlaceholderCompleted(placeholderKey, placeholderFormValues)) {
      return false
    }

    if (userFilters?.errorOnly && errors.filter(({ key }) => placeholderKey === key).length === 0) {
      return false
    }

    if (filters && templateFilter && !filters.includes(templateFilter)) {
      return false
    }

    return true
  })

export const filterPlaceholdersByCategory = (
  placeholders: IPlaceholderConfig[],
  placeholderCategory: string
): IPlaceholderConfig[] => placeholders.filter(({ category }) => category === placeholderCategory)

export const getSubTabs = (
  placeholders: IPlaceholderConfig[],
  category: string,
  placeholderFormValues: IPlaceholderFormValue[],
  errors: IPageContainerFormError[]
): Tab[] =>
  filterPlaceholdersByCategory(placeholders, category).map(({ placeholderKey, type }) => {
    const isError = errors.filter(({ key }) => placeholderKey === key).length > 0
    return {
      icon: <PlaceholderTypeIcon type={type} />,
      isChecked: isPlaceholderCompleted(placeholderKey, placeholderFormValues) && !isError,
      isError,
      label: placeholderKey,
      name: placeholderKey,
    }
  })

interface ITranslationRuleError {
  errors: [string, number][]
  value?: number
}

export const getTranslationErrors = ({
  rules,
  translation,
}: {
  rules?: IPlaceholderRule[]
  translation: ITranslationKeyValue
}): { maxLength: ITranslationRuleError; minLength: ITranslationRuleError } | undefined => {
  const maxLength = rules?.find(({ type }) => type === PLACEHOLDER_RULE_TYPES.MAX_LENGTH)?.value

  const minLength = rules?.find(({ type }) => type === PLACEHOLDER_RULE_TYPES.MIN_LENGTH)?.value

  if (Array.isArray(maxLength) || Array.isArray(minLength)) {
    return undefined
  }

  const lengthsArray = Object.entries(translation.lengths)

  const parsedMaxLength = typeof maxLength === 'string' ? parseInt(maxLength) : maxLength

  const parsedMinLength = typeof minLength === 'string' ? parseInt(minLength) : minLength

  const maxLengthErrors =
    (parsedMaxLength && lengthsArray.filter(([, length]) => parsedMaxLength < (length as number))) || []

  const minLengthErrors =
    (parsedMinLength && lengthsArray.filter(([, length]) => parsedMinLength > (length as number))) || []

  return {
    maxLength: { errors: maxLengthErrors, value: parsedMaxLength },
    minLength: { errors: minLengthErrors, value: parsedMinLength },
  }
}

export const getTranslationErrorMessage = ({
  rules,
  translation,
}: {
  rules?: IPlaceholderRule[]
  translation: ITranslationKeyValue
}): string => {
  const errors = getTranslationErrors({ rules, translation })

  if (errors) {
    const { maxLength, minLength } = errors

    if (minLength.errors.length) {
      return 'Min length not met'
    }

    if (maxLength.errors.length) {
      return 'Max length exceeded'
    }
  }

  return ''
}

export const getAssetErrors = ({
  asset,
  rules,
}: {
  asset: IAssetOption
  rules?: IPlaceholderRule[]
}): { qualityIdentifiers: string[] } | undefined => {
  const rule = rules?.find(({ type }) => type === PLACEHOLDER_RULE_TYPES.REQUIRED_QUALITY_IDENTIFIER)

  if (!rule) {
    return undefined
  }

  const assetQualityIdentifiers = asset.files?.map(({ quality_identifier }) => quality_identifier) || []

  const ruleQualityIdentifiers = Array.isArray(rule.value) ? rule.value : []

  const errors = ruleQualityIdentifiers.filter((el) => !assetQualityIdentifiers.includes(el))

  return {
    qualityIdentifiers: errors,
  }
}

export const getAssetErrorMessage = ({ qualityIdentifiers }: { qualityIdentifiers: string[] }): string => {
  return `Quality identifiers missing (${qualityIdentifiers.join(', ')})`
}
